import type { HttpResponse, HttpResponseBase } from '@angular/common/http';
import type { OptionSelect } from '@models/commons/option-select';
import type { SelectServiceResponse } from '@models/partner/partner-services/services-list/select-service';
import type { AlgoliaService, Service } from '@models/service/service-admin.interface';
import type { ServiceCreationDTO, ServiceGlobal } from '@models/service/service-global.interface';
import type { SearchParamsObject } from 'algoliasearch';
import type { Observable } from 'rxjs';
import type { PartialDeep } from 'type-fest';

import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { NotificationsService, NotifStatus } from '@commons/bo-ui';
import { environment } from '@env/environment';
import { AlgoliaClientProvider } from '@loyalty/utils-angular/algolia';
import { catchError, from, map, of, switchMap } from 'rxjs';

type PlanGroupObject = {
  option: string;
  value: string;
  checked: boolean;
};

export type SelectPlanGroupsResponse = PlanGroupObject[];

@Injectable({ providedIn: 'root' })
export class ServiceWebservice {
  readonly #http = inject(HttpClient);
  readonly #algoliaClientProvider = inject(AlgoliaClientProvider);
  readonly #notificationsService = inject(NotificationsService);

  patch(serviceId: string, servicePatch: PartialDeep<ServiceGlobal>): Observable<ServiceGlobal> {
    return this.#http.patch<ServiceGlobal>(`${environment.api.obendy}/v4/services/${serviceId}`, servicePatch);
  }

  create(service: ServiceCreationDTO): Observable<ServiceGlobal> {
    return this.#http.post<ServiceGlobal>(`${environment.api.obendy}/v4/services`, service);
  }

  checkIsDeletable(serviceId: string): Observable<[string]> {
    return this.#http.get<[string]>(`${environment.api.obendy}/v3/services/${serviceId}/check-is-deletable`);
  }

  delete(serviceId: string): Observable<HttpResponse<{ status: string }>> {
    return this.#http.delete<{ status: string }>(`${environment.api.obendy}/v3/services/${serviceId}`, {
      observe: 'response',
    });
  }

  getPartnerServiceSelect(partnerId: string): Observable<OptionSelect[]> {
    return this.#http.get<OptionSelect[]>(`${environment.api.obendy}/v1/partner-revision/${partnerId}/services-select`);
  }

  getServicesList(
    query: string,
    searchParams: Pick<
      SearchParamsObject,
      'page' | 'hitsPerPage' | 'facetFilters' | 'aroundLatLng' | 'aroundRadius' | 'filters' | 'facets'
    >
  ): Observable<{ services: Service[]; length: number }> {
    return this.#getClient().pipe(
      switchMap((client) =>
        from(
          client.searchSingleIndex<AlgoliaService>(
            {
              indexName: environment.algolia.index.services,
              searchParams: {
                query,
                ...searchParams,
              },
            },
            {
              cacheable: false,
            }
          )
        )
      ),
      map((response) => ({
        services: response.hits.map((service) => ({
          ...service,
          id: service.objectID,
          logo: service.defaultConfiguration.assets.logo,
        })),
        length: response.nbHits ?? 0,
      }))
    );
  }

  getServicesListByPartner(partnerId: string): Observable<Service[]> {
    return this.#http.get<Service[]>(`${environment.api.obendy}/v1/partner-revision/${partnerId}/services`).pipe(
      catchError(() => {
        this.#notificationsService.createToast('Erreur lors de la récupération des services', NotifStatus.Failed, 5000);
        return of([]);
      })
    );
  }

  countServicesInPlanGroups(partnerId: string, serviceId: string): Observable<number> {
    return this.#http
      .head<HttpResponseBase>(
        `${environment.api.obendy}/v1/partner-revision/${partnerId}/services/${serviceId}/count-plan-groups`,
        { observe: 'response' }
      )
      .pipe(
        map(({ headers }) => {
          const planGroupsCount = headers.get('X-Count-Service-Plan-Groups');
          if (planGroupsCount === null) throw new Error('invalid header X-Count-Service-Plan-Groups');
          return Number(planGroupsCount);
        })
      );
  }

  getServicesSelect({
    partnerId,
    planGroupId,
  }: {
    partnerId: string;
    planGroupId: string;
  }): Observable<SelectServiceResponse> {
    const endPoint = `${environment.api.obendy}/v1/partner-revision/${partnerId}/plan-groups/${planGroupId}/services-select`;
    return this.#http.get<SelectServiceResponse>(endPoint);
  }

  getServicePlanGroupsSelect({
    partnerId,
    serviceId,
  }: {
    partnerId: string;
    serviceId: string;
  }): Observable<SelectPlanGroupsResponse> {
    const endPoint = `${environment.api.obendy}/v1/partner-revision/${partnerId}/services/${serviceId}/plan-groups-select`;
    return this.#http.get<SelectPlanGroupsResponse>(endPoint);
  }

  #getClient() {
    return this.#algoliaClientProvider.getClient(environment.algolia.clientName)!;
  }
}
